import React, { useEffect, useState } from 'react';
import { Zap, TrendingDown, Users, Shield } from 'lucide-react';
import DealCard from '../components/DealCard';
import ComingSoonBanner from '../components/ComingSoonBanner';
import { getDeals, BUSINESS, DEALS_TEASER_MODE, DEALS_TEASER_MESSAGE } from '../config';

const TEASER_DEALS = [
  {
    deal_id: 'teaser-laptop-pro',
    title: 'PC Portable Pro 14" reconditionné',
    base_price: 549,
    current_price: 449,
    status: 'active',
    min_threshold: 10,
    confirmed_quantity: 6,
  },
  {
    deal_id: 'teaser-screen-4k',
    title: 'Écran 27" 4K reconditionné',
    base_price: 299,
    current_price: 229,
    status: 'active',
    min_threshold: 8,
    confirmed_quantity: 3,
  },
  {
    deal_id: 'teaser-mini-pc',
    title: 'Mini-PC bureautique reconditionné',
    base_price: 399,
    current_price: 319,
    status: 'active',
    min_threshold: 12,
    confirmed_quantity: 9,
  },
];

export default function Deals() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | active | completed

  useEffect(() => {
    document.title = `Deals Groupes | ${BUSINESS.name}`;

    if (DEALS_TEASER_MODE) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const params = {};
        if (filter === 'active') params.status = 'active';
        if (filter === 'completed') params.status = 'completed';
        const data = await getDeals(params);
        setDeals(data?.deals || []);
      } catch (e) {
        console.error('Failed to load deals', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filter]);

  const activeDeals = deals.filter((d) => d.status === 'active');
  const completedDeals = deals.filter((d) => ['validated', 'delivered'].includes(d.status));

  const filters = [
    { key: 'all', label: 'Tous' },
    { key: 'active', label: 'En cours' },
    { key: 'completed', label: 'Terminés' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-primary py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/5 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-secondary/10 border border-secondary/30 rounded-full px-4 py-1.5 mb-6">
            <Zap className="w-4 h-4 text-secondary" />
            <span className="font-accent text-xs uppercase tracking-widest text-secondary font-medium">Deals Groupés</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white font-heading mb-4">
            Achetez ensemble,<br />
            <span className="text-secondary">payez moins</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Rejoignez un deal de groupe pour obtenir des prix dégressifs sur du matériel
            informatique reconditionné. Plus on est nombreux, moins c'est cher.
          </p>

          {/* Trust signals */}
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <TrendingDown className="w-4 h-4 text-secondary" />
              <span>Prix dégressifs</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Users className="w-4 h-4 text-secondary" />
              <span>Achat collectif</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Shield className="w-4 h-4 text-secondary" />
              <span>Garanti ou remboursé</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CONTENU (fond slate-50, cadre V4 cohérent avec le catalogue) ─── */}
      <section className="bg-slate-50 py-10 md:py-14 min-h-[40vh] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {DEALS_TEASER_MODE && (
            <div className="mb-10">
              <ComingSoonBanner
                title="Section en cours de construction"
                message={DEALS_TEASER_MESSAGE}
              />
            </div>
          )}

          {/* Filtres (cachés en mode teaser) — pills V4 rounded-sm uppercase */}
          {!DEALS_TEASER_MODE && (
            <div className="flex flex-wrap gap-2 mb-8">
              {filters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-4 py-2 rounded-sm font-accent text-xs font-semibold uppercase tracking-wider border transition-colors ${
                    filter === f.key
                      ? 'bg-secondary text-white border-secondary'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-secondary hover:text-secondary'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}

          {DEALS_TEASER_MODE ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" aria-hidden="true">
              {TEASER_DEALS.map((deal) => (
                <DealCard key={deal.deal_id} deal={deal} comingSoon />
              ))}
            </div>
          ) : loading ? (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : deals.length === 0 ? (
            <div className="text-center py-16">
              <Zap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Aucun deal pour le moment.</p>
            </div>
          ) : (
            <>
              {/* Active deals */}
              {(filter === 'all' || filter === 'active') && activeDeals.length > 0 && (
                <div className="mb-12">
                  {filter === 'all' && (
                    <h2 className="text-2xl font-semibold uppercase tracking-tight text-primary font-heading mb-6">
                      Deals en cours
                    </h2>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activeDeals.map((deal) => (
                      <DealCard key={deal.deal_id} deal={deal} />
                    ))}
                  </div>
                </div>
              )}

              {/* Completed deals */}
              {(filter === 'all' || filter === 'completed') && completedDeals.length > 0 && (
                <div>
                  {filter === 'all' && (
                    <h2 className="text-2xl font-semibold uppercase tracking-tight text-primary font-heading mb-6">
                      Deals terminés
                    </h2>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {completedDeals.map((deal) => (
                      <DealCard key={deal.deal_id} deal={deal} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
